import type { CardDetail, CustomerProfileView } from '@voll-intelligence/domain';

import { env } from '@web/core/env';
import { t } from '@web/i18n';
import { ChannelChip, CustomerAvatar, ToneChip, VipBadge } from '@web/ui/core/molecules';

import type { TravelerAccess } from './traveler_side';

/** Who the traveler is (from the card + dossier contacts) and the way out to the ticket. */
export function TravelerViewHeader({
  detail,
  profile,
  access,
}: Readonly<{
  detail: CardDetail | undefined;
  profile: CustomerProfileView | undefined;
  access: TravelerAccess;
}>): React.JSX.Element {
  return (
    <header className="flex items-center gap-3 border-b border-border-soft bg-surface px-5 py-3">
      {detail ? (
        <>
          <HeaderIdentity detail={detail} profile={profile} />
          <HeaderTicket detail={detail} />
        </>
      ) : access === 'loading' ? (
        <span className="flex-1 text-base text-muted-foreground">
          {t('travelerView.header.loading')}
        </span>
      ) : (
        <span className="flex-1 text-base font-medium text-foreground">
          {t('travelerView.header.unknownTraveler')}
        </span>
      )}
    </header>
  );
}

function HeaderIdentity({
  detail,
  profile,
}: Readonly<{ detail: CardDetail; profile: CustomerProfileView | undefined }>): React.JSX.Element {
  const facts = [detail.company.name, profile?.email, profile?.phone].filter(
    (v): v is string => typeof v === 'string' && v.length > 0,
  );
  return (
    <>
      <CustomerAvatar name={detail.customer.name} vip={detail.customer.vip} size="md" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2">
          <span className="truncate text-base font-medium text-foreground">
            {detail.customer.name}
          </span>
          {detail.customer.vip ? <VipBadge /> : null}
          <span className="font-mono text-tiny uppercase tracking-eyebrow text-faint-foreground">
            #{detail.seq}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-meta text-muted-foreground">
          <span className="truncate">{facts.join(' · ')}</span>
          <ChannelChip channel={detail.originChannel} />
        </div>
      </div>
    </>
  );
}

function HeaderTicket({ detail }: Readonly<{ detail: CardDetail }>): React.JSX.Element | null {
  const ticket = detail.externalRefs.find((r) => r.system === 'zendesk' && r.type === 'ticket');
  if (!ticket) return null;
  return (
    <a
      className="shrink-0"
      href={`${env.VITE_ZENDESK_URL}/agent/tickets/${ticket.externalId}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      <ToneChip tone="gray" icon="link-simple">
        {t('card.zendeskTicket', { id: ticket.externalId })}
      </ToneChip>
    </a>
  );
}
